/**
 * Orbit Navigation Utility
 * 
 * When a tap indicator is active (user tapped a 3D point), horizontal pan
 * orbits around that point by cycling through cameras that see it, ordered
 * by viewing angle. Near cameras are preferred; far cameras serve as fallback
 * with auto-zoom compensation to keep the object the same apparent size.
 */

import { calculateCameraSpaceDepth } from './ThumbnailScaleCalculator'

/** If a nearest-neighbor step exceeds GAP_MULTIPLIER × median step distance, truncate the chain there. */
const GAP_MULTIPLIER = 3

interface Camera {
  position: [number, number, number]
  rotation?: [number, number, number, number]
  intrinsics: { width: number; height: number; fx?: number; fy?: number }
}

export interface OrbitEntry {
  frameIndex: number
  angle: number       // Viewing angle around the 3D point (radians)
  depth: number       // Camera-space depth to the 3D point (meters)
  isNear: boolean     // True if within near-depth window
  projectedPixel?: [number, number]  // Where the 3D point projects in this camera's image (px, py)
}

/**
 * Build an orbit sequence: cameras sorted by viewing angle around a 3D point.
 * 
 * Algorithm:
 * 1. For each camera that projects the point, compute angle and depth
 * 2. Define "near" as depth <= minDepth * nearMultiplier
 * 3. Bucket cameras into angular sectors (~15°)
 * 4. Per sector: prefer near camera (best centrality), fall back to far
 * 5. Sort final list by angle
 * 6. Rotate so the tapped camera is first
 * 
 * @param point3D - The 3D point being orbited
 * @param projections - Map<frameIndex, [pixelX, pixelY]> from tap indicator
 * @param cameras - Full camera array from store
 * @param tappedFrameIndex - Frame where user originally tapped
 * @param sectorDegrees - Angular bucket size (default 15°)
 * @param nearMultiplier - Depth threshold multiplier (default 1.5)
 */
export function buildOrbitSequence(
  point3D: [number, number, number],
  projections: Map<number, [number, number]>,
  cameras: Camera[],
  tappedFrameIndex: number,
  sectorDegrees: number = 15,
  nearMultiplier: number = 1.5,
  includeAllCameras: boolean = false
): OrbitEntry[] {
  if (!point3D || projections.size < 2) return []

  // Step 1: Compute angle and depth for each camera
  // When includeAllCameras=true, iterate over ALL cameras (not just in-bounds projections)
  const entries: OrbitEntry[] = []
  let minDepth = Infinity

  const frameIndices = includeAllCameras
    ? cameras.map((_, i) => i)
    : Array.from(projections.keys())

  for (const frameIndex of frameIndices) {
    const camera = cameras[frameIndex]
    if (!camera) continue

    // Filter: only include frames where the 3D point projects into the center portion of the frame.
    // Always keep the tapped frame regardless of where the tap landed.
    // When sectorDegrees=0 (explore mode), skip this filter — use all cameras with valid projections.
    if (sectorDegrees !== 0 && frameIndex !== tappedFrameIndex) {
      const proj = projections.get(frameIndex)
      if (proj) {
        const [px, py] = proj
        const { width, height } = camera.intrinsics
        const inCenterX = px >= width * 0.25 && px <= width * 0.75
        const inCenterY = py >= height * 0.25 && py <= height * 0.75
        if (!inCenterX || !inCenterY) continue
      }
    }

    const depth = calculateCameraSpaceDepth(point3D, camera)

    // Compute viewing angle: atan2 of camera position relative to point
    // Project onto XY plane for horizontal orbit
    const dx = camera.position[0] - point3D[0]
    const dy = camera.position[1] - point3D[1]
    const angle = Math.atan2(dy, dx) // radians, -π to π

    entries.push({ frameIndex, angle, depth, isNear: false })
    if (depth < minDepth) minDepth = depth
  }

  if (entries.length === 0) return []

  // Step 2: Mark near/far cameras
  const nearThreshold = minDepth * nearMultiplier
  for (const entry of entries) {
    entry.isNear = entry.depth <= nearThreshold
  }

  // Step 3-4: Bucket into sectors & pick best per sector
  // When sectorDegrees=0, skip bucketing and use all cameras (fine-grained orbit)
  let sequence: OrbitEntry[]
  if (sectorDegrees === 0) {
    sequence = [...entries]
  } else {
    const sectorRadians = (sectorDegrees * Math.PI) / 180
    const sectorMap = new Map<number, OrbitEntry>()

    for (const entry of entries) {
      // Normalize angle to [0, 2π) for consistent bucketing
      const normalizedAngle = ((entry.angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)
      const sectorKey = Math.floor(normalizedAngle / sectorRadians)

      const existing = sectorMap.get(sectorKey)
      if (!existing) {
        sectorMap.set(sectorKey, entry)
      } else {
        // Prefer near over far; among same tier prefer closer depth
        if (entry.isNear && !existing.isNear) {
          sectorMap.set(sectorKey, entry)
        } else if (entry.isNear === existing.isNear && entry.depth < existing.depth) {
          sectorMap.set(sectorKey, entry)
        }
      }
    }
    sequence = Array.from(sectorMap.values())
  }

  // Step 5: Build nearest-neighbor chain through 3D camera positions.
  // Starting from tappedFrameIndex, greedily pick the closest unvisited camera
  // at each step. This produces a smooth spatial walk regardless of camera layout
  // (straight line, curved path, cylinder inside/outside, irregular).
  const chain: OrbitEntry[] = []
  const remaining = new Set(sequence.map((_, i) => i)) // indices into sequence[]

  // Find tapped frame in sequence as the starting point
  let currentIdx = sequence.findIndex(e => e.frameIndex === tappedFrameIndex)
  if (currentIdx < 0) currentIdx = 0

  // Start the chain with the tapped camera
  chain.push(sequence[currentIdx])
  remaining.delete(currentIdx)

  // Greedily add nearest unvisited camera
  while (remaining.size > 0) {
    const currentPos = cameras[chain[chain.length - 1].frameIndex].position
    let bestIdx = -1
    let bestDist = Infinity

    for (const idx of remaining) {
      const pos = cameras[sequence[idx].frameIndex].position
      const dx = pos[0] - currentPos[0]
      const dy = pos[1] - currentPos[1]
      const dz = pos[2] - currentPos[2]
      const dist = dx * dx + dy * dy + dz * dz // squared distance (skip sqrt)
      if (dist < bestDist) {
        bestDist = dist
        bestIdx = idx
      }
    }

    chain.push(sequence[bestIdx])
    remaining.delete(bestIdx)
  }

  // Step 6: Gap detection — truncate chain where inter-camera distance jumps.
  // Compute 3D distances between consecutive chain entries.
  if (chain.length > 2) {
    const stepDistances: number[] = []
    for (let i = 0; i < chain.length - 1; i++) {
      const posA = cameras[chain[i].frameIndex].position
      const posB = cameras[chain[i + 1].frameIndex].position
      const dx = posB[0] - posA[0]
      const dy = posB[1] - posA[1]
      const dz = posB[2] - posA[2]
      stepDistances.push(Math.sqrt(dx * dx + dy * dy + dz * dz))
    }

    // Find median step distance
    const sorted = [...stepDistances].sort((a, b) => a - b)
    const median = sorted[Math.floor(sorted.length / 2)]
    const threshold = median * GAP_MULTIPLIER

    // Find first gap (but keep at least 2 entries: tapped + 1 neighbor)
    let breakIndex = -1
    for (let i = 0; i < stepDistances.length; i++) {
      if (stepDistances[i] > threshold && i + 1 >= 2) {
        breakIndex = i
        break
      }
    }

    if (breakIndex !== -1) {
      const originalLen = chain.length
      chain.length = breakIndex + 1 // keep entries [0..breakIndex]
      console.log(`🔗 [Orbit] Chain gap at step ${breakIndex}: ${stepDistances[breakIndex].toFixed(3)}m > ${threshold.toFixed(3)}m (median ${median.toFixed(3)}m × ${GAP_MULTIPLIER}). Truncated ${originalLen} → ${chain.length}.`)
    }

    // Log chain with inter-step distances
    const distLabels = chain.map((e, i) => {
      const dist = i < stepDistances.length ? ` --(${stepDistances[i].toFixed(3)}m)-->` : ''
      return `${e.frameIndex}${dist}`
    }).join(' ')
    console.log(`🔗 [Orbit] Chain: ${distLabels}`)
  }

  return chain
}

/**
 * Step through the orbit sequence.
 * @param direction +1 for clockwise (swipe left), -1 for counter-clockwise (swipe right)
 * @returns New orbit index (wraps around)
 */
export function stepOrbit(sequenceLength: number, currentIndex: number, direction: 1 | -1): number {
  if (sequenceLength === 0) return 0
  return Math.max(0, Math.min(sequenceLength - 1, currentIndex + direction))
}

/**
 * Calculate zoom compensation for orbit: keeps the tapped object
 * the same apparent size across near and far cameras.
 * 
 * Formula: zoom = tappedDepth / currentDepth
 * Near cameras (same depth) → zoom ≈ 1.0
 * Far cameras (2× depth)   → zoom ≈ 2.0
 * 
 * @param tappedDepth - Depth at the originally tapped camera
 * @param currentDepth - Depth at the current orbit camera
 * @param maxZoom - Maximum zoom cap (default 3.5)
 * @param minZoom - Minimum zoom floor (default 0.5)
 */
export function calculateOrbitZoom(
  tappedDepth: number,
  currentDepth: number,
  maxZoom: number = 3.5,
  minZoom: number = 0.5
): number {
  if (currentDepth <= 0 || tappedDepth <= 0) return 1.0
  const zoom = tappedDepth / currentDepth
  return Math.max(minZoom, Math.min(maxZoom, zoom))
}

/** Horizontal pixel threshold to trigger one orbit step */
export const ORBIT_STEP_THRESHOLD_PX = 50
